import { useEffect, useRef, useState } from "react"
import { Close, Search } from "@mui/icons-material"
import { Box, IconButton, InputAdornment, TextField } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useDebouncedCallback } from "use-debounce"

import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"

type StyleProps = Readonly<{
    isActiveInput: boolean
    activeWidth: string
    inactiveWidth: string
}>

const useStyles = makeStyles<StyleProps>()((theme, { isActiveInput, activeWidth, inactiveWidth }) => ({
    searchWrapper: {
        display: "flex",
        alignItems: "center",
        transition: "width 0.3s ease",
        overflow: "hidden",
        width: isActiveInput ? activeWidth : inactiveWidth,
    },
    search: {
        "& .MuiOutlinedInput-root": {
            padding: "0 .5rem",
            borderRadius: ".75rem",
            cursor: "pointer",
            boxShadow: "0px 2px 1px -1px rgba(0, 0, 0, 0.12)",
        },
        "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
            transition: "all 0.3s ease-out",
            borderWidth: "1px",
        },
        "& .MuiSvgIcon-root": {
            color: theme.palette.tailwind.slate[800],
        },
        "& .MuiInputBase-input": {
            height: "1.2rem",
        },
    },
    disabledContent: {
        pointerEvents: "none",

        "& .MuiSvgIcon-root": {
            color: "rgba(0, 0, 0, 0.26)",
        },
    },
}))

type FilterSearchProps = Readonly<{
    search: string
    setSearch: (searchText: string) => void
    disabled?: boolean
    activeWidth?: string
    inactiveWidth?: string
    debounceMs?: number
    defaultActive?: boolean
}>

export function FilterSearch({
    search,
    setSearch,
    disabled = false,
    activeWidth = "9rem",
    inactiveWidth = "2.5rem",
    debounceMs = 300,
    defaultActive = false,
}: FilterSearchProps) {
    const [inputValue, setInputValue] = useState(search)
    const [isActiveInput, setIsActiveInput] = useState(defaultActive)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const { classes, cx } = useStyles({ isActiveInput, activeWidth, inactiveWidth })

    const debouncedPublishSearch = useDebouncedCallback((value: string) => {
        setSearch(value)
    }, debounceMs)

    useEffect(() => {
        debouncedPublishSearch(inputValue)
    }, [inputValue, debouncedPublishSearch])

    useEffect(() => {
        return () => {
            debouncedPublishSearch.cancel()
        }
    }, [debouncedPublishSearch])

    useEffect(() => {
        setInputValue((prev) => (search === prev ? prev : search))
    }, [search])

    const handleClear = () => {
        debouncedPublishSearch.cancel()
        setInputValue("")
        setSearch("")
        if (!defaultActive) {
            setIsActiveInput(false)
            inputRef.current?.blur()
        }
    }

    return (
        <Box className={cx(classes.searchWrapper, { [classes.disabledContent]: disabled })}>
            <LocalizedStrict id="create-order-add-search-input" attrs={{ placeholder: true }}>
                <TextField
                    name="search"
                    value={inputValue}
                    size="small"
                    fullWidth
                    placeholder="Search"
                    inputRef={inputRef}
                    onFocus={() => setIsActiveInput(true)}
                    onBlur={() => {
                        debouncedPublishSearch.flush()
                        if (!defaultActive && !inputValue.trim()) setIsActiveInput(false)
                    }}
                    onChange={(e) => setInputValue(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position="start"
                                onClick={() => {
                                    inputRef.current?.focus()
                                    setIsActiveInput(true)
                                }}
                            >
                                <Search />
                            </InputAdornment>
                        ),
                        endAdornment: inputValue.trim().length > 0 && (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClear} size="small" edge="end">
                                    <Close fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    disabled={disabled}
                    className={classes.search}
                />
            </LocalizedStrict>
        </Box>
    )
}
